<script lang="ts" setup>
import { useDisplay } from 'vuetify'
import type { NavItems } from '@/interfaces/nav'
import { getComputedNavLinkToProp, isNavLinkActive } from '@/composable/useNav'
import { useAuthStore } from '@/stores/auth'

defineProps<{
  navItems: NavItems
}>()

const { lgAndUp, xlAndUp } = useDisplay()

const authStore = useAuthStore()

const subMenuOpen = ref(false)
</script>

<template>
  <VList
    class="nav-items"
    :density="xlAndUp ? 'default' : 'compact'"
  >
    <template v-if="authStore.authenticated">
      <VListItem
        v-for="navItem in navItems"
        :key="navItem.title"
        class="nav-link"
        :class="xlAndUp ? 'nav-link--xl' : ''"
      >
        <template v-if="navItem.subNav">
          <VMenu>
            <template #activator="{ props }">
              <VBtn
                class="nav-link__btn"
                color="secondary"
                :prepend-icon="lgAndUp ? navItem.icon : undefined"
                v-bind="props"
              >
                <span class="nav-item-title">{{ navItem.title }} <VIcon
                  icon="mdi-chevron-down"
                /></span>
              </VBtn>
            </template>
            <VList>
              <VListItem
                v-for="subNavItem in navItem.subNav"
                :key="subNavItem.title"
                :prepend-icon="subNavItem.icon"
                :href="subNavItem.href ?? ''"
                :to="subNavItem.to ?? ''"
                :target="subNavItem.target ?? '_self'"
              >
                <span class="nav-item-title">{{ subNavItem.title }}</span>
                <template
                  v-if="subNavItem.subNavAdditional"
                  #append
                >
                  <VIcon
                    icon="mdi-menu-right"
                    size="x-small"
                  />
                </template>

                <VMenu
                  v-if="subNavItem.subNavAdditional"
                  v-model="subMenuOpen"
                  :open-on-focus="false"
                  activator="parent"
                  location="end"
                  open-on-hover
                  submenu
                >
                  <VList>
                    <VListItem
                      v-for="subNavAdditionalItem in subNavItem.subNavAdditional"
                      :key="subNavAdditionalItem.title"
                      :color="subNavAdditionalItem.color"
                      :to="subNavAdditionalItem.to ?? ''"
                      :href="subNavAdditionalItem.href ?? ''"
                      :target="subNavAdditionalItem.target ?? '_self'"
                      :active="false"
                      @click="subMenuOpen = false"
                    >
                      <VListItemTitle :style="{ color: subNavAdditionalItem.color }">
                        {{ subNavAdditionalItem.title }}
                      </VListItemTitle>
                    </VListItem>
                  </VList>
                </VMenu>
              </VListItem>
            </VList>
          </VMenu>
        </template>
        <template v-else>
          <Component
            :is="navItem.to ? 'RouterLink' : 'a'"
            v-bind="getComputedNavLinkToProp(navItem)"
            class="text-decoration-none"
            :class="{ 'router-link-active router-link-exact-active': isNavLinkActive(navItem, $router) }"
            :target="navItem.target ?? '_self'"
          >
            <VBtn
              :prepend-icon="lgAndUp ? navItem.icon : undefined"
              class="nav-link__btn"
              color="secondary"
            >
              <span class="nav-item-title">{{ navItem.title }}</span>
            </VBtn>
          </Component>
        </template>
      </VListItem>
    </template>
  </VList>
</template>
